// O8 · First help (QO8_FirstHelp, F_O8 since 0.2.0). The buddy asks for the one thing in words that follow the O3
// answer (obHello: "What's one thing you started and keep drifting away from?" for Keeping going, F_O8Hello); the user
// holds to talk (or types); the server turns it into a 2-minute first card (POST /api/onboarding/first-help, voice via
// /api/stt/public).
// Under the card: Start, "Make it smaller" (swap to fh.smaller), and two links:
// - "Not quite" (feedback 7): "Not quite." + the buddy's fixed line, then Haven't started / Partway through / Almost done,
//   or hold to talk / type what's off. The old card dims, the new help comes in under it, and it can be done again.
//   The request carries `where` (the newest button answer), `fix` (their words) and `card` (the card turned down).
// - "I can't do it right now" -> reason -> a prep step now plus the real task first on tomorrow's list, or (reason
//   'none') picking a time IS the first card. It works on the card on screen (with `where`, so it fits that card).
import { Fragment } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { CharId, FirstHelp, FirstWhere } from '@shared/types';
import { C } from '@shared/content/characters';
import { LINES, OB_CANT_REASONS, OB_NOT_QUITE, OB_NOT_QUITE_LINE, OB_WHERE, obHello } from '@shared/content/lines';
import { fmtTime } from '@shared/rules';
import {
  Screen, XScene, XCard, OnbTopBar, Bubble, TypingBubble, TalkButton, PrimaryButton, SecondaryButton, TextButton, MessageCard, Icon, ScrollArea,
  Waveform, useHoldToTalk,
} from '../../components';
import { api } from '../../api/client';
import type { AudioPayload, CantReason } from '../../api/types';
import type { RecorderError } from '../../audio/recorder';
import { errorText, toast } from '../../store';
import { back, go } from '../../router';
import { LATER_TIMES, firstNote } from '../../content/onboarding';
import { say } from '../../lib/say';
import { activeHelp, answers, draftStore, lastWhere, progress, setDraft, useDraft, type FirstLater, type FixRound } from './draft';
import { micError } from './Questions';
import s from './O8FirstHelp.module.css';

type Busy = null | 'stt' | 'help' | 'reason' | 'fix' | 'fixstt';

/** "Change the time" options: the server's suggestion first, then the prototype's cycle. */
function laterOptions(fh2: FirstHelp | null): { time: string; at: number }[] {
  const t = fh2?.tomorrow;
  const list = t ? [{ time: t.time || fmtTime(t.at), at: t.at }] : [];
  for (const x of LATER_TIMES) if (!list.some((y) => y.at === x.at)) list.push(x);
  return list;
}

const WHERE_LABEL = Object.fromEntries(OB_WHERE.map((w) => [w.id, w.label])) as Record<FirstWhere, string>;
const isWhere = (x: unknown): x is FirstWhere => x === 'new' || x === 'middle' || x === 'almost';

/** The buddy's reply and the card under it. A card they said "Not quite" to stays in the chat, dimmed. */
function HelpCard({ ch, reply, title, dim, titleSize }: { ch: CharId; reply: string; title: string; dim: boolean; titleSize: number }) {
  return (
    <>
      <Bubble from="buddy" ch={ch} face e={3} faceSize={32} anim="xmRise 380ms ease-out both">{reply}</Bubble>
      {/* The drop animation ends at full opacity, so the dimming sits on an inner element. */}
      <div class={s.card} style={{ animation: 'xmDrop 650ms 200ms ease-out both' }}>
        <div class={s.cardDim} data-dim={dim ? 'true' : 'false'}>
          <XCard ch={ch} w={342} h={170} label="YOUR FIRST CARD · 2 MIN" title={title} titleSize={titleSize} tilt={-1} />
        </div>
      </div>
    </>
  );
}

/** "Not quite" answer by voice (F_O8): a pill next to "Type instead". Hold to talk, or tap to start and stop. While
 *  the mic is on, the pill takes the whole row (there is nothing to type then, and 360px needs the room). */
function TalkPill({ name, busy, onAudio, onError, onTooShort, onType }: {
  name: string; busy: boolean; onAudio: (clip: AudioPayload) => Promise<unknown>; onError: (e: RecorderError) => void; onTooShort: () => void;
  onType: () => void;
}) {
  const t = useHoldToTalk({ tap: 'toggle', onAudio, onError, onTooShort, disabled: busy });
  const live = t.phase === 'starting' || t.phase === 'recording' || t.phase === 'cancel';
  const label = busy || t.phase === 'sending' ? 'One moment…'
    : t.phase === 'starting' ? 'Listening…'
    : t.phase === 'recording' ? (t.toggled ? 'Listening… tap to send' : 'Listening… let go to send')
    : t.phase === 'cancel' ? 'Release to cancel'
    : 'Hold to talk';
  return (
    <div class={s.nqTalk}>
      <button
        type="button" class={s.pill} data-live={live ? 'true' : 'false'} aria-pressed={live ? 'true' : 'false'}
        aria-label={live ? 'Stop and send' : `Hold to talk to ${name}`} {...t.bind} disabled={busy}
      >
        {live ? <Waveform levels={t.levels} bars={8} height={18} color="var(--on)" /> : <Icon name="mic" size={20} color="var(--acc)" />}
        <span>{label}</span>
      </button>
      {!live && <button type="button" class={s.typeLink} onClick={onType}>Type instead</button>}
    </div>
  );
}

export function O8FirstHelp() {
  const d = useDraft((x) => x);
  const ch: CharId = d.ch || 'margo';
  const c = C[ch];
  const L = LINES[ch];
  const [busy, setBusy] = useState<Busy>(null);
  const [failed, setFailed] = useState<null | 'help' | 'reason' | 'fix'>(null);
  const [typing, setTyping] = useState(false);
  const [text, setText] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const abort = useRef<AbortController | null>(null);
  useEffect(() => () => abort.current?.abort(), []);
  useEffect(() => { if (typing) input.current?.focus(); }, [typing]);

  const said = d.said;
  const fh = d.fh;
  const fixes = d.fixes;
  const last = fixes.length ? fixes[fixes.length - 1] : null;
  // "Not quite" tapped, no answer yet / answered, the new help on its way (or it failed).
  const nqOpen = !!last && !last.where && !last.words;
  const fixWaiting = !!last && !nqOpen && !last.fh;
  // The help on screen now (the newest one); null while one is on its way.
  const act = activeHelp(d);
  const cardTitle = act ? (d.smaller ? act.smaller : act.card) : '';
  const reason = d.reason;
  const reasonDef = OB_CANT_REASONS.find((r) => r.id === reason) || null;
  const fh2 = d.fh2;
  const isNone = reason === 'none';
  const picked = !!(reason && fh2);
  const later = laterOptions(fh2);
  const laterNow = later[d.laterIdx % Math.max(1, later.length)] || null;
  const sv = { n: c.name, task: fh?.thing || said };
  const thing = fh?.thing || said || '';

  const ask = async (t: string) => {
    const said = t.trim();
    if (!said) return;
    setTyping(false); setText(''); setFailed(null);
    setBusy('help');
    setDraft({ said, fh: null, fixes: [], smaller: false, cant: false, reason: null, fh2: null });
    abort.current?.abort();
    const ctl = new AbortController(); abort.current = ctl;
    try {
      const r = await api.firstHelp({ invite: d.invite, ch, ob: answers(), text: said }, ctl.signal);
      setDraft({ fh: r });
    } catch (e) {
      if (!ctl.signal.aborted) { setFailed('help'); toast(errorText(e)); }
    } finally { if (abort.current === ctl) setBusy(null); }
  };

  const onAudio = async (clip: { b64: string; mime: string }) => {
    setBusy('stt');
    try {
      const r = await api.sttPublic({ invite: d.invite, audio: { b64: clip.b64, mime: clip.mime }, hint: 'onboarding' });
      const t = (r.text || '').trim();
      setBusy(null);
      if (!t) { toast("Didn't catch that. Try again, or type it."); return; }
      await ask(t);
    } catch (e) { setBusy(null); toast(errorText(e)); }
  };

  // ------------------------------------------------------------ Not quite
  const notQuite = () => {
    if (!act) return;
    setTyping(false); setText(''); setFailed(null);
    setDraft({ fixes: [...fixes, { card: cardTitle, where: null, words: null, fh: null }], smaller: false });
  };

  /** Ask for a new help for the newest round. Their newest button answer is `where`; their words (when the newest
   *  answer is in words: everything they said in "Not quite" so far) are `fix`; `card` is the card they turned down
   *  and `turnedDown` the ones from earlier rounds (none of them comes back). */
  const requestFix = async (rounds: FixRound[]) => {
    const cur = rounds[rounds.length - 1];
    if (!cur) return;
    const where = lastWhere({ ...draftStore.get(), fixes: rounds });
    const words = cur.words ? rounds.map((r) => r.words).filter(Boolean).join(' ').slice(0, 500) : '';
    const turnedDown = rounds.slice(0, -1).map((r) => r.card);
    setBusy('fix'); setFailed(null);
    abort.current?.abort();
    const ctl = new AbortController(); abort.current = ctl;
    try {
      const r = await api.firstHelp({
        invite: d.invite, ch, ob: answers(), text: said || thing, thing, card: cur.card,
        ...(where ? { where } : {}), ...(words ? { fix: words } : {}), ...(turnedDown.length ? { turnedDown } : {}),
      }, ctl.signal);
      const now = draftStore.get().fixes;
      setDraft({ fixes: now.map((x, i) => (i === now.length - 1 ? { ...x, fh: r } : x)), smaller: false });
    } catch (e) {
      if (!ctl.signal.aborted) { setFailed('fix'); toast(errorText(e)); }
    } finally { if (abort.current === ctl) setBusy(null); }
  };

  const answerFix = async (a: { where?: FirstWhere; words?: string }) => {
    const where = a.where || null;
    const words = (a.words || '').trim().replace(/\s+/g, ' ').slice(0, 500) || null;
    const rounds = draftStore.get().fixes;
    if ((!where && !words) || !rounds.length) return;
    setTyping(false); setText('');
    const next = rounds.map((r, i) => (i === rounds.length - 1 ? { ...r, where, words, fh: null } : r));
    setDraft({ fixes: next });
    await requestFix(next);
  };

  const onFixAudio = async (clip: AudioPayload) => {
    setBusy('fixstt');
    try {
      const r = await api.sttPublic({ invite: d.invite, audio: { b64: clip.b64, mime: clip.mime }, hint: 'notquite' });
      const t = (r.text || '').trim();
      setBusy(null);
      if (!t) { toast("Didn't catch that. Try again, or tap one."); return; }
      await answerFix({ words: t });
    } catch (e) { setBusy(null); toast(errorText(e)); }
  };

  /** Back to the three answers (after a failed request: "Say it again"). */
  const reopenFix = () => {
    const rounds = draftStore.get().fixes;
    setFailed(null);
    setDraft({ fixes: rounds.map((r, i) => (i === rounds.length - 1 ? { ...r, where: null, words: null, fh: null } : r)) });
  };

  // ------------------------------------------------------------ I can't do it right now
  const pickReason = async (id: CantReason) => {
    setDraft({ reason: id, fh2: null, laterIdx: 0 });
    setBusy('reason'); setFailed(null);
    abort.current?.abort();
    const ctl = new AbortController(); abort.current = ctl;
    // Where they are with it: what they said in "Not quite", else what the card on screen went on.
    const stage = act?.stage;
    const where = lastWhere(d) || (isWhere(stage) ? stage : null);
    try {
      const r = await api.firstHelp({ invite: d.invite, ch, ob: answers(), reason: id, thing, ...(where ? { where } : {}) }, ctl.signal);
      setDraft({ fh2: r });
    } catch (e) {
      if (!ctl.signal.aborted) { setFailed('reason'); toast(errorText(e)); }
    } finally { if (abort.current === ctl) setBusy(null); }
  };

  const start = () => {
    setDraft({ workCard: cardTitle, evenSmaller: null, first: null });
    go('O9');
  };

  const startPrep = () => {
    if (!fh2?.prep || !laterNow) return;
    const t = fh2.tomorrow;
    const l: FirstLater = { time: laterNow.time, at: laterNow.at, title: t?.title || thing, min: t?.min || 25, when: 'TOMORROW' };
    // prep.label is the NOW card's header ('NOW · 2 MIN'); the done card's note comes from the reason.
    setDraft({ workCard: fh2.prep.card, evenSmaller: null, first: { card: fh2.prep.card, done: false, doneAt: null, minutes: null, note: firstNote(thing), time: '2 min', later: l } });
    go('O9');
  };

  const pickTime = (opt: { label: string; at: number; day: 'today' | 'tomorrow' }) => {
    if (!fh2?.pickTime) return;
    const t = fh2.tomorrow;
    const l: FirstLater = { time: fmtTime(opt.at), at: opt.at, title: t?.title || thing, min: t?.min || 25, when: opt.day === 'today' ? 'TONIGHT' : 'TOMORROW' };
    setDraft({ workCard: fh2.pickTime.card, first: { card: fh2.pickTime.card, done: true, doneAt: new Date().toISOString(), minutes: null, note: firstNote(thing, true), time: '10 sec', later: l } });
    go('O10');
  };

  const replyText = fh2 ? (isNone ? fh2.pickTime?.line || fh2.reply || say(L.none, sv) : fh2.prep?.line || fh2.reply || say(L.prep, sv)) : '';
  const nowTitle = fh2 ? (isNone ? fh2.pickTime?.card || '' : fh2.prep?.card || '') : '';
  // A request that isn't running and has no answer (it failed, or the page reloaded while it ran) can be tried again.
  const helpFailed = !!said && !fh && busy !== 'help';
  const fixFailed = fixWaiting && busy !== 'fix';

  // ------------------------------------------------------------ bottom controls by phase
  const typeBar = (onSend: () => void) => (
    <form class={s.typeBar} onSubmit={(e) => { e.preventDefault(); onSend(); }}>
      <input
        ref={input} type="text" aria-label="Your answer" placeholder={`Message ${c.name}`} value={text} enterKeyHint="send"
        onInput={(e) => setText((e.currentTarget as HTMLInputElement).value)} autoComplete="off"
        // Nothing typed and the keyboard put away: back to talking (or the three answers).
        onBlur={(e) => { if (!(e.currentTarget as HTMLInputElement).value.trim()) setTyping(false); }}
      />
      <button type="submit" class={s.send} aria-label="Send" aria-disabled={text.trim() ? undefined : 'true'}><Icon name="send" size={20} strokeWidth={2.4} /></button>
    </form>
  );

  let controls = null;
  let ctlPad = 18;
  if (!said && !typing) {
    controls = (
      <div class={s.talk}>
        <TalkButton
          name={c.name} onAudio={onAudio} disabled={busy === 'stt'}
          idleLabel={busy === 'stt' ? 'One moment…' : 'Hold to talk'}
          onError={(e) => { micError(e, 'You can type instead.'); setTyping(true); }}
          onTooShort={() => toast('Hold to talk, or tap once to start and once to stop.')}
        />
        <TextButton weight={600} size={14} onClick={() => setTyping(true)} style={{ minHeight: 40 }}>Type instead</TextButton>
      </div>
    );
  } else if (!said && typing) {
    ctlPad = 24;
    controls = typeBar(() => void ask(text));
  } else if (said && !fh && helpFailed) {
    ctlPad = 18;
    controls = (
      <div class={s.row2}>
        <PrimaryButton h={56} grow={1.35} onClick={() => void ask(said)}>Try again</PrimaryButton>
        <SecondaryButton h={56} grow={1} onClick={() => setDraft({ said: null, fh: null, fixes: [] })}>Say it again</SecondaryButton>
      </div>
    );
  } else if (nqOpen && !typing) {
    ctlPad = 10;
    controls = (
      <div class={s.nq} style={{ animation: 'xmRise 400ms 450ms ease-out both' }}>
        <div class={s.chips}>
          {OB_WHERE.map((w) => <button key={w.id} type="button" class={s.chip} onClick={() => void answerFix({ where: w.id })}>{w.label}</button>)}
        </div>
        <TalkPill
          name={c.name} busy={busy === 'fixstt'} onAudio={onFixAudio} onType={() => setTyping(true)}
          onError={(e) => { micError(e, 'You can type instead.'); setTyping(true); }}
          onTooShort={() => toast('Hold to talk, or tap once to start and once to stop.')}
        />
      </div>
    );
  } else if (nqOpen && typing) {
    ctlPad = 24;
    controls = typeBar(() => void answerFix({ words: text }));
  } else if (fixFailed) {
    ctlPad = 18;
    controls = (
      <div class={s.row2}>
        <PrimaryButton h={56} grow={1.35} onClick={() => void requestFix(draftStore.get().fixes)}>Try again</PrimaryButton>
        <SecondaryButton h={56} grow={1} onClick={reopenFix}>Say it again</SecondaryButton>
      </div>
    );
  } else if (act && !d.cant && !fixWaiting) {
    ctlPad = 10;
    controls = (
      <div class={s.main}>
        <div class={s.row2}>
          <PrimaryButton h={56} icon="play" grow={1.35} onClick={start} data-cta="start">Start</PrimaryButton>
          <SecondaryButton h={56} grow={1} onClick={() => setDraft({ smaller: !d.smaller })}>{d.smaller ? 'Back to the first one' : 'Make it smaller'}</SecondaryButton>
        </div>
        <div class={s.links}>
          <TextButton underline onClick={notQuite} style={{ padding: '0 8px' }}>Not quite</TextButton>
          <span class={s.dot} aria-hidden="true" />
          <TextButton underline onClick={() => setDraft({ cant: true, reason: null, fh2: null })} style={{ padding: '0 8px' }}>I can't do it right now</TextButton>
        </div>
      </div>
    );
  } else if (d.cant && !reason) {
    ctlPad = 10;
    controls = (
      <div class={s.reasons} style={{ animation: 'xmRise 400ms 450ms ease-out both' }}>
        {OB_CANT_REASONS.map((r) => <button key={r.id} type="button" class={s.reason} onClick={() => void pickReason(r.id)}>{r.label}</button>)}
      </div>
    );
  } else if (reason && !fh2 && (failed === 'reason' || busy !== 'reason')) {
    controls = (
      <div class={s.row2}>
        <PrimaryButton h={56} grow={1.35} onClick={() => void pickReason(reason)}>Try again</PrimaryButton>
        <SecondaryButton h={56} grow={1} onClick={() => setDraft({ reason: null })}>Pick another</SecondaryButton>
      </div>
    );
  } else if (picked && !isNone) {
    controls = (
      <div class={s.row2} style={{ animation: 'xmRise 400ms 700ms ease-out both' }}>
        <PrimaryButton h={56} icon="play" grow={1.35} onClick={startPrep} data-cta="start">Start</PrimaryButton>
        <SecondaryButton h={56} grow={1} onClick={() => setDraft({ laterIdx: (d.laterIdx + 1) % Math.max(1, later.length) })}>Change the time</SecondaryButton>
      </div>
    );
  } else if (picked && isNone) {
    controls = (
      <div class={s.times} style={{ animation: 'xmRise 400ms 700ms ease-out both' }}>
        {(fh2?.pickTime?.options || []).map((o) => <button key={o.label} type="button" class={s.time} onClick={() => pickTime(o)}>{o.label}</button>)}
      </div>
    );
  }

  return (
    <Screen theme={ch} id="O8">
      <div class={s.top} aria-hidden="true">
        <XScene ch={ch} stage={1} mode="clear" pos="50% 28%" alt="" />
        <div class={s.topFade} />
      </div>
      <OnbTopBar pct={progress('O8', !!said)} onBack={() => back('O7')} overScene />
      <ScrollArea class={s.chat} reverse fadeTop={30} fadeBottom={16}>
        <div class={s.log} role="log" aria-live="polite" aria-label={`Chat with ${c.name}`}>
          <Bubble from="buddy" ch={ch} face e={1} faceSize={32}>{say(obHello(ch, d.ob.stuck), sv)}</Bubble>
          {said && <Bubble from="user" anim="xmRise 320ms ease-out both">{said}</Bubble>}
          {said && !fh && busy === 'help' && <TypingBubble ch={ch} faceSize={32} />}
          {fh && (
            <HelpCard
              ch={ch} reply={fh.reply || say(L.firstReply, sv)} titleSize={24}
              title={fixes.length ? fixes[0].card : cardTitle} dim={fixes.length > 0}
            />
          )}
          {fixes.map((r, i) => {
            const newest = i === fixes.length - 1;
            return (
              <Fragment key={i}>
                <Bubble from="user" anim="xmRise 320ms ease-out both">{OB_NOT_QUITE}</Bubble>
                <Bubble from="buddy" ch={ch} face e={4} faceSize={32} anim="xmRise 380ms 250ms ease-out both">{say(OB_NOT_QUITE_LINE[ch], sv)}</Bubble>
                {(r.where || r.words) && <Bubble from="user" anim="xmRise 320ms ease-out both">{r.where ? `${WHERE_LABEL[r.where]}.` : r.words}</Bubble>}
                {newest && !r.fh && busy === 'fix' && <TypingBubble ch={ch} faceSize={32} />}
                {r.fh && (
                  <HelpCard
                    ch={ch} reply={r.fh.reply || say(L.firstReply, sv)} titleSize={22}
                    title={newest ? cardTitle : fixes[i + 1].card} dim={!newest}
                  />
                )}
              </Fragment>
            );
          })}
          {d.cant && act && (
            <>
              <Bubble from="user" anim="xmRise 320ms ease-out both">I can't do it right now.</Bubble>
              <Bubble from="buddy" ch={ch} face e={4} faceSize={32} anim="xmRise 380ms 250ms ease-out both">{say(L.askWhy, sv)}</Bubble>
            </>
          )}
          {reasonDef && (
            <>
              <Bubble from="user" anim="xmRise 320ms ease-out both">{reasonDef.label + '.'}</Bubble>
              {!fh2 && busy === 'reason' && <TypingBubble ch={ch} faceSize={32} />}
            </>
          )}
          {picked && (
            <>
              <Bubble from="buddy" ch={ch} face e={3} faceSize={32} anim="xmRise 380ms 250ms ease-out both">{replyText}</Bubble>
              <div class={s.card} style={{ animation: 'xmDrop 650ms 350ms ease-out both' }}>
                <XCard ch={ch} w={342} h={150} label={isNone ? 'YOUR FIRST CARD · 10 SEC' : fh2?.prep?.label || 'NOW · 2 MIN'} title={nowTitle} titleSize={22} tilt={-1} />
              </div>
              {!isNone && laterNow && (
                <MessageCard variant="dashed" style={{ alignSelf: 'center', width: 'min(342px, 100%)', display: 'flex', flexDirection: 'column', gap: 3, animation: 'xmRise 380ms 600ms ease-out both' }}>
                  <span class={s.laterHead}>{`FIRST ON TOMORROW'S LIST · ${laterNow.time}`}</span>
                  <span class={s.laterTitle}>{fh2?.tomorrow?.title || fh?.thing}</span>
                </MessageCard>
              )}
            </>
          )}
        </div>
      </ScrollArea>
      {controls && <div class={s.controls} style={{ paddingBottom: `calc(var(--xm-bottom) + ${ctlPad}px)` }}>{controls}</div>}
    </Screen>
  );
}
